// Kit registration

import {useEffect, useState} from 'react';
import {NextPage} from 'next';
import {useRouter} from 'next/router';

import {useRegisterQuery} from '#apollo/schema';
import {FullscreenLoading, FullscreenModalHeader} from '#component/partial';
import {EventType, PageViewEvent, useAnalytics} from '#component/provider/AnalyticsProvider';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {sprinkles} from '#style/sprinkles';

import {StepBarcodeInput} from './component/StepBarcodeInput';
import {StepIntro} from './component/StepIntro';
import {StepReview} from './component/StepReview';
import {StepSummary} from './component/StepSummary';

import {StepConfig, StepKey, StepProps, WizardData} from './Register.types';

import * as css from './Register.css';

const STEP_CONFIG: StepConfig[] = [
  {component: StepIntro, key: 'intro', analyticsName: 'Registration Intro'},
  {component: StepBarcodeInput, key: 'barcode-input', analyticsName: 'Barcode Input'},
  {component: StepReview, key: 'review', analyticsName: 'Review Kit'},
  {component: StepSummary, key: 'summary', analyticsName: 'Registration Progress'},
];

export const RegisterScreen = () => {
  const {track} = useAnalytics();
  const {data, previousData} = useRegisterQuery();
  const {query} = useRouter();
  const initialStep = typeof query.initialStep === 'string' ? parseInt(query.initialStep) : 0;
  const [isReady, setIsReady] = useState<boolean>(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(initialStep);
  const [wizardData, setWizardData] = useState<Partial<WizardData>>({});
  const showCloseButton = currentStepIndex === 0 || currentStepIndex === STEP_CONFIG.length - 1;
  const currentStep = STEP_CONFIG[currentStepIndex];

  useEffect(() => {
    window?.scroll({top: 0, left: 0, behavior: 'smooth'});
  }, [currentStepIndex]);

  useEffect(() => {
    if (!isReady && !previousData && !!data) {
      setIsReady(true);
    }
  }, [isReady, data, previousData]);

  useEffect(() => {
    if (isReady) {
      track<PageViewEvent>({
        evtName: EventType.PageView,
        pageName: currentStep.analyticsName,
      });
    }
  }, [track, isReady, currentStep]);

  const handleNext: StepProps['onNext'] = data => {
    if (data) {
      // Merge new data into currentData
      setWizardData(currentData => ({...currentData, ...data}));
    }

    setCurrentStepIndex(cur => Math.min(cur + 1, STEP_CONFIG.length - 1));
  };

  const handlePrev = (stepKey?: StepKey) => {
    setCurrentStepIndex(cur => {
      // Default to going back one step
      let nextStep = cur - 1;

      // If we provide a `stepKey` try to jump to that step instead
      const stepToJumpTo = STEP_CONFIG.find(({key}) => key === stepKey);
      if (stepToJumpTo) {
        nextStep = STEP_CONFIG.indexOf(stepToJumpTo);
      }

      return Math.max(nextStep, 0);
    });
  };

  if (!isReady) {
    return <FullscreenLoading />;
  }

  return (
    <div className={css.layout}>
      <div className={css.bg({highlight: currentStepIndex === 0})} />

      {/* Wrap the header in a div to help us position it (relatively) */}
      <div className={css.header}>
        <FullscreenModalHeader
          logoColor={currentStep?.logoColor || 'cotton'}
          buttonVariant="secondary-dark"
          className={sprinkles({w: 'full'})}
          hideCloseButton={!showCloseButton}
        />
      </div>

      <currentStep.component
        onNext={handleNext}
        onPrev={handlePrev}
        data={data}
        wizardData={wizardData}
      />
    </div>
  );
};

export const Register: NextPage = withRequireUserState(RegisterScreen, {
  userState: 'authenticated',
  path: '/signin?return_to=/register',
});
